import React from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  Box,
  Container,
  Typography,
  Paper,
  Button,
  Breadcrumbs,
  Link,
  Card,
  CardContent,
  Chip,
} from '@mui/material';
import { ArrowBack } from '@mui/icons-material';
import { useGetAgentsForAccountQuery } from '../store/api/synthflowAgentsApi';
import { useGetActiveSynthflowAccountsQuery } from '../store/api/synthflowApi';
import NewCampaignForm from '../components/outbound-calling/NewCampaignForm';

const CreateCampaignPage: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  
  // Get agent information for breadcrumb
  const agentId = searchParams.get('agent_id');
  const accountId = searchParams.get('account_id');
  
  // Debug logging
  console.log('CreateCampaignPage Debug:', {
    agentId,
    accountId,
    searchParams: Object.fromEntries(searchParams.entries())
  });
  
  // Get accounts and agents for breadcrumb
  const { data: accounts = [] } = useGetActiveSynthflowAccountsQuery();
  const { data: agents } = useGetAgentsForAccountQuery(accountId ? parseInt(accountId) : 1, {
    skip: !accountId && !agentId
  });

  const handleSuccess = () => {
    navigate(`/outbound-calling?agent_id=${agentId}&account_id=${accountId}`);
  };

  const handleCancel = () => {
    navigate(`/outbound-calling?agent_id=${agentId}&account_id=${accountId}`);
  };

  return (
    <Container maxWidth="lg" sx={{ py: 2 }}>
      {/* Breadcrumbs */}
      <Breadcrumbs sx={{ mb: 2 }}>
        <Link
          component="button"
          variant="body1"
          onClick={() => navigate('/aigent/agents')}
          sx={{ textDecoration: 'none' }}
        >
          {accounts.find((acc: any) => acc.id === parseInt(accountId || '1'))?.name || 'Account'}
        </Link>
        {agentId && agents && (
          <Link
            component="button"
            variant="body1"
            onClick={() => navigate('/aigent/agents')}
            sx={{ textDecoration: 'none' }}
          >
            {(() => {
              const agent = agents.find((agent: any) => agent.id === parseInt(agentId));
              console.log('Agent Debug:', { agentId, agent, agents });
              return agent?.name || `Agent ${agentId}`;
            })()}
          </Link>
        )}
        <Link
          component="button"
          variant="body1"
          onClick={() => navigate(`/outbound-calling?agent_id=${agentId}&account_id=${accountId}`)}
          sx={{ textDecoration: 'none' }}
        >
          Campaigns
        </Link>
        <Typography color="text.primary">Create Campaign</Typography>
      </Breadcrumbs>

      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Box>
          <Typography variant="h4" component="h1" gutterBottom>
            Create New Campaign
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Set up an automated outbound calling campaign with your contacts and agents.
          </Typography>
        </Box>
        <Button
          variant="outlined"
          startIcon={<ArrowBack />}
          onClick={() => navigate(`/outbound-calling?agent_id=${agentId}&account_id=${accountId}`)}
        >
          Back to Campaigns
        </Button>
      </Box>

      {/* Account and Agent Information */}
      {(agentId || accountId) && (
        <Card sx={{ mb: 3, bgcolor: 'primary.light', color: 'primary.contrastText' }}>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Campaign Configuration
            </Typography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, alignItems: 'center' }}>
              {accountId && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                    Account:
                  </Typography>
                  <Chip 
                    label={accounts.find((acc: any) => acc.id === parseInt(accountId))?.name || `Account ${accountId}`}
                    size="small"
                    sx={{ 
                      bgcolor: 'primary.main', 
                      color: 'primary.contrastText',
                      fontWeight: 'bold'
                    }}
                  />
                </Box>
              )}
              {agentId && agents && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                    Agent:
                  </Typography>
                  <Chip 
                    label={agents.find((agent: any) => agent.id === parseInt(agentId))?.name || `Agent ${agentId}`}
                    size="small"
                    sx={{ 
                      bgcolor: 'primary.main', 
                      color: 'primary.contrastText',
                      fontWeight: 'bold'
                    }}
                  />
                </Box>
              )}
            </Box>
            <Typography variant="body2" sx={{ mt: 1, opacity: 0.9 }}>
              This campaign will be created for the selected agent and will use the agent's configuration for making calls.
            </Typography>
          </CardContent>
        </Card>
      )}

      {/* Campaign Form */}
      <Paper sx={{ p: 2 }}>
        <NewCampaignForm onSuccess={handleSuccess} onCancel={handleCancel} />
      </Paper>
    </Container>
  );
};

export default CreateCampaignPage;
